import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_STEPS } from '@/hooks/contest/contestSteps.js';
import { REWARD_TYPE } from "@/hooks/contest/contestStatus.js";
import { triggerConfetti } from "@/utils/animations.js";
import { Copy, Check, ExternalLink, Mail, Instagram, Facebook } from 'lucide-react';
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const SOCIAL_ICONS = {
    tiktok:    <TikTokIcon />,
    instagram: <Instagram size={20} strokeWidth={2} />,
    twitter:   <XIcon />,
    facebook:  <Facebook size={20} strokeWidth={2} />,
};

export const DemoStepResult = ({
    contestId,
    status,
    reward,
    pseudo,
    triggerAnimation = true,
    isPreview = false,
}) => {
    const { t } = useTranslation();
    const { brand, result, demoContest } = useBrandConfig();
    const googlePlaceId = demoContest?.place?.googlePlaceId;
    const googleReviewUrl = googlePlaceId
        ? `https://search.google.com/local/writereview?placeid=${googlePlaceId}`
        : null;

    const [copied, setCopied] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const isWinner = status === APP_STEPS.RESULT_WIN;

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        if (isWinner && reward && triggerAnimation) {
            triggerConfetti(result.confettiColors);
        }
        return () => clearTimeout(timer);
    }, [isWinner, reward, triggerAnimation, result.confettiColors]);

    const calculateTimeLeft = (expiryDate) => {
        if (!expiryDate) return null;
        const distance = new Date(expiryDate).getTime() - new Date().getTime();
        if (distance < 0) return 'EXPIRED';
        const h = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((distance % (1000 * 60)) / 1000);
        return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    };

    const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(reward?.expiresAt));

    useEffect(() => {
        if (!reward?.expiresAt) return;
        const timer = setInterval(() => {
            const nextTime = calculateTimeLeft(reward.expiresAt);
            setTimeLeft(nextTime);
            if (nextTime === 'EXPIRED') clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [reward?.expiresAt]);

    const isPhysicalBigPrize = isWinner && reward?.type === REWARD_TYPE.PHYSICAL;
    let codeToCopy = null;
    if (!isPhysicalBigPrize) {
        codeToCopy = reward?.promoCode || reward?.code;
        if (codeToCopy === 'LOSE-SHOT') codeToCopy = 'SQUAD-10';
        if (codeToCopy === 'WIN-DRINK') codeToCopy = 'HOLY-WIN';
    }

    const handleCopy = () => {
        if (codeToCopy) {
            navigator.clipboard.writeText(codeToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const getRewardDisplay = (reward) => {
        if (!reward) return { label: "PAS DE LOT." };
        switch (reward.type) {
            case REWARD_TYPE.DISCOUNT_PERCENT: return { label: `-${reward.value}% SUR LE SITE` };
            case REWARD_TYPE.DISCOUNT_AMOUNT:  return { label: `-${reward.value}€ OFFERTS` };
            case REWARD_TYPE.POINTS:           return { label: `+${reward.value} POINTS` };
            default: return { label: reward.title || "TON LOT" };
        }
    };

    const rewardInfo = getRewardDisplay(reward);

    const customStyles = `
        .stagger-1 { transition-delay: 100ms; }
        .stagger-2 { transition-delay: 200ms; }
        .stagger-3 { transition-delay: 300ms; }
        .stagger-35 { transition-delay: 380ms; }
        .stagger-4 { transition-delay: 460ms; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .bg-dots { background-size: 24px 24px; background-image: radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px); }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col" style={{ backgroundColor: brand.colors.pageBg }}>
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* HEADER */}
            <div className="w-full pt-16 pb-3 flex justify-center items-center z-20 relative bg-white/20 backdrop-blur-md shrink-0 border-b border-white/30">
                <BrandLogo className="h-16 object-contain" />
            </div>

            {/* CONTENU */}
            <div className="flex-1 overflow-y-auto pb-48 hide-scrollbar relative bg-dots px-6 cursor-default">
                <div className="flex flex-col items-center min-h-full max-w-sm mx-auto py-8 gap-8">

                    {/* Titre */}
                    <div className={`text-center flex flex-col items-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-1`}>
                        <h1 className="font-heading text-[4rem] leading-[0.85] text-black uppercase italic tracking-tighter drop-shadow-sm">
                            {isWinner ? t('holy.result.win_title_1') : t('holy.result.lose_title_1')}<br/>
                            <span className="text-white drop-shadow-md">
                                {isWinner ? t('holy.result.win_title_2') : t('holy.result.lose_title_2')}
                            </span>
                        </h1>
                    </div>

                    {/* Bloc Récompense */}
                    <div className={`relative w-full transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-2`}>
                        <div className="relative overflow-hidden bg-white/30 backdrop-blur-3xl rounded-[2.5rem] p-8 shadow-[0_15px_50px_-5px_rgba(0,0,0,0.1)] border border-white/40 flex flex-col items-center text-center">

                            <h2 className="font-heading text-[2.5rem] uppercase text-black leading-none tracking-tighter italic mb-2">
                                {rewardInfo.label}
                            </h2>

                            {isPhysicalBigPrize ? (
                                <div className="w-full mt-4 flex flex-col items-center">
                                    <p className="text-[12px] font-black uppercase tracking-widest mb-6 px-2" style={{ color: brand.colors.accent }}>
                                        {t('holy.result.winner_message', { pseudo })}
                                    </p>
                                    <div className="bg-white/60 backdrop-blur-md rounded-2xl p-5 w-full flex flex-col items-center justify-center shadow-inner border border-white/80">
                                        <Mail size={32} className="text-black mb-3" strokeWidth={1.5} />
                                        <p className="text-[10px] font-black text-black/80 uppercase tracking-widest leading-relaxed">
                                            {t('holy.result.physical_prize_email')}
                                        </p>
                                        <p className="text-[9px] font-bold text-black/50 uppercase tracking-widest mt-2">
                                            {t('holy.result.spam_hint')}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-[11px] font-bold text-black/50 uppercase tracking-widest mt-1 mb-6 px-2">
                                        {isWinner
                                            ? t('holy.result.winner_message', { pseudo })
                                            : t('holy.result.loser_message')}
                                    </p>

                                    {codeToCopy && (
                                        <div className="w-full mt-2">
                                            <div className="text-[9px] font-black text-black/40 uppercase tracking-[0.2em] mb-2">
                                                {isWinner ? t('holy.result.code_winner_label') : t('holy.result.code_loser_label')}
                                            </div>
                                            <div
                                                onClick={handleCopy}
                                                className="relative bg-white/60 backdrop-blur-md rounded-2xl p-5 flex flex-col items-center justify-center cursor-pointer shadow-inner border border-white/80 hover:bg-white/80 transition-all active:scale-95"
                                            >
                                                <span className="text-[2rem] font-heading text-black tracking-wider select-all mt-1">{codeToCopy}</span>
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-black/30">
                                                    {copied
                                                        ? <Check size={20} style={{ color: brand.colors.accent }} />
                                                        : <Copy size={20} />
                                                    }
                                                </div>
                                                {copied && (
                                                    <div className="absolute -top-3 right-0 text-black text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-sm animate-fade-in" style={{ backgroundColor: brand.colors.accent }}>
                                                        {t('holy.result.copied')}
                                                    </div>
                                                )}
                                            </div>
                                            {reward?.expiresAt && timeLeft !== 'EXPIRED' && (
                                                <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-[#FF003C] bg-[#FF003C]/10 px-3 py-1.5 rounded-full inline-block animate-pulse">
                                                    {t('holy.result.expires_in', { timeLeft })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Bloc Google Reviews */}
                    {googleReviewUrl && (
                        <div className={`w-full transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-3`}>
                            <a
                                href={googleReviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col items-center gap-3 w-full bg-white rounded-[2rem] p-5 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12)] border border-white/80 active:scale-[0.98] transition-transform"
                            >
                                {/* Header Google */}
                                <div className="flex items-center gap-2">
                                    {/* Logo G */}
                                    <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                    </svg>
                                    <span className="text-[13px] font-black text-gray-700 uppercase tracking-wider">Google</span>
                                </div>

                                {/* Étoiles */}
                                <div className="flex items-center gap-1">
                                    {[1,2,3,4,5].map((i) => (
                                        <svg key={i} viewBox="0 0 24 24" fill="#FBBC05" className="w-8 h-8 drop-shadow-sm">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                    ))}
                                </div>

                                {/* Texte */}
                                <div className="text-center">
                                    <p className="font-heading text-xl font-black text-gray-900 uppercase italic tracking-tight leading-tight">
                                        Laisser un avis
                                    </p>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                        Ça nous aide beaucoup !
                                    </p>
                                </div>
                            </a>
                        </div>
                    )}

                    {/* Bloc Social */}
                    {result.socials?.length > 0 && (
                        <div className={`w-full flex flex-col items-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-3`}>
                            <div className="bg-white/30 backdrop-blur-3xl border border-white/40 rounded-3xl p-5 w-full shadow-[0_15px_50px_-5px_rgba(0,0,0,0.05)] text-center">
                                <h3 className="font-heading italic text-2xl uppercase text-black mb-1">
                                    {t('holy.result.social_title')}
                                </h3>
                                <p className="text-[9px] font-black text-black/50 uppercase tracking-widest mb-4">
                                    {t('holy.result.social_subtitle')}
                                </p>
                                <div className="flex items-center justify-center gap-3">
                                    {result.socials.map(({ platform, url }) => (
                                        <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                                            className="w-12 h-12 bg-white/60 hover:bg-white rounded-full flex items-center justify-center text-black transition-all shadow-sm border border-white/80 hover:scale-110 active:scale-95"
                                        >
                                            {SOCIAL_ICONS[platform] ?? null}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CTA STICKY */}
            <div
                className={`absolute bottom-0 left-0 w-full px-6 pb-8 pt-16 to-transparent z-30 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-4`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg}, ${brand.colors.pageBg} 60%, transparent)` }}
            >
                <div className="pointer-events-none max-w-sm mx-auto">
                    <a
                        href={brand.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pointer-events-auto group w-full flex items-center justify-center gap-3 py-5 rounded-full font-heading text-2xl uppercase italic tracking-wider shadow-lg active:translate-y-1 active:shadow-none transition-all cursor-pointer"
                        style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                    >
                        {t('holy.result.shop_cta')}
                        <ExternalLink size={24} strokeWidth={3} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </a>
                </div>
            </div>
        </div>
    );
};
